import './ChatBot.scss';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowRight,
  ArrowUp,
  CaretDown,
  Chats,
  CheckCircle,
  Copy,
  Gear,
  Globe,
  Link,
  Microphone,
  Notebook,
  NotePencil,
  Play,
  Plus,
  SpeakerHigh,
  SpeakerSlash,
  Stop,
  TextAlignJustify,
  Trash,
  XCircle,
} from '@phosphor-icons/react';
import { User } from '../../util/Types/User';
import { io, Socket } from 'socket.io-client';
import { useStoreTabs } from '../../store/storeTabs';
import { FlowService } from '../../service/FlowService';
import { IFlowChatActions, IFlowModel } from '../../data/models/flow';
import { IFluxBotModel } from '../../data/models/flux-bot';
import { FluxbotDropdown } from './_components/Fluxbot-dropdown';
import { generateRandomId } from '../../utils/generateId';
import { History } from './_components/History';
import { LinkComponent } from './LinkComponent';
import { ChatbotCodeComponent } from './CodeComponent';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { FluxService } from '../../service/FluxService';
import { useNotificationStore } from '../../store/storeNotifications';
import { ChatSocketResponse } from '../../data/responses/chatbot';
import PublishChatModal from './PublishChatModal/PublishChatModal';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useLocation } from 'react-router-dom';
import {
  ChatContainer,
  ChatContent,
  ChatHeader,
  InputContainer,
  MessageBubble,
  MessageContainer,
  MessagesContainer,
  TypingIndicator,
} from './_components/Styled-components';
import { WaveformVisualizer } from './_components/WaveformVisualizer';
import { ChatbotService } from '../../service/ChatbotService';
import { IChatbotFlowModel } from '../../data/models/chatbot';
import { flowToChatbotMapper } from '../../utils/flow-to-chatbot-mapper';

type ExpansionLevel = 'middle' | 'full';

export type ActiveTab = {
  id: string;
  name?: string;
  space?: string;
};

type Props = {
  onCreateChat: () => void;
  authUser: User;
  sideBarOpenWidth: number;
};

interface IChat {
  id: string;
  name: string;
  entries: IEntry[];
}

interface IEntry {
  role: string;
  content: string;
  timestamp: string;
  actions?: IFlowChatActions[];
}

const MemoizedLinkComponent = memo(LinkComponent);

const MessageContent = memo(
  ({
    content,
    messageTimestamp,
    isNewMessage,
    isChatRespondingWithVoice,
  }: {
    content: string;
    messageTimestamp: string;
    isNewMessage: boolean;
    isChatRespondingWithVoice: boolean;
  }) => {
    return (
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        remarkPlugins={[remarkGfm]}
        components={{
          img: ({ node, ...props }) => (
            <img
              {...props}
              alt={props.alt ?? 'Image'}
              className="max-w-full my-2 rounded-md"
            />
          ),
          audio: ({ node, ...props }) => (
            <>
              <audio controls className="my-2">
                <track kind="captions" />
                <source src={props.src} />
                Your browser does not support the audio element.
              </audio>
              <br />
            </>
          ),
          a: props => (
            <MemoizedLinkComponent
              {...props}
              id={messageTimestamp}
              autoPlay={isChatRespondingWithVoice}
              isNewMessage={isNewMessage}
            />
          ),
          code: props => <ChatbotCodeComponent {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    );
  },
);

const ChatBot = ({ onCreateChat, authUser, sideBarOpenWidth }: Props) => {
  const { setNotification } = useNotificationStore((state: any) => state);
  const location = useLocation();
  const tabs = useStoreTabs((state: any) => state.tabs);

  const isAbleToShowTabs = location.pathname.toLowerCase().includes('flow');

  const audioRecognitionRef = useRef<SpeechRecognition>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const tabDropdownRef = useRef<HTMLDivElement>(null);
  const settingsPopoverAnchorEl = useRef<HTMLButtonElement | null>(null);
  const historyPopoverAnchorEl = useRef<HTMLButtonElement | null>(null);

  const [socketClient, setSocketClient] = useState<Socket | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [isLoadingFluxBots, setIsLoadingFluxBots] = useState(false);
  const [isLoadingChatbot, setIsLoadingChatbot] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isHistoryPopoverOpen, setIsHistoryPopoverOpen] = useState(false);
  const [isSettingsPopoverOpen, setIsSettingsPopoverOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFluxbotDropdownPopoverOpen, setIsFluxbotDropdownPopoverOpen] =
    useState(false);
  const [expasionMode, setExpasionMode] = useState<ExpansionLevel>('middle');
  const [activeTab, setActiveTab] = useState<ActiveTab>({
    id: '',
    name: '',
    space: '',
  });
  const [isTyping, setIsTyping] = useState(false);
  const [fluxBots, setFluxBots] = useState<IFluxBotModel[]>([]);

  const [chatbot, setChatbot] = useState<IChatbotFlowModel>(
    {} as IChatbotFlowModel,
  );

  const [dropdownValues, setDropdownValues] = useState<string[]>([]);

  const [session, setSession] = useState<string>('primary');

  const [isRecording, setIsRecording] = useState(false);
  const [isChatRespondingWithVoice, setIsChatRespondingWithVoice] =
    useState(false);
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const toggleExpansionMode = () => {
    setExpasionMode(prev => (prev === 'middle' ? 'full' : 'middle'));
  };

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 300);
  }, []);

  const getFluxBots = async () => {
    setIsLoadingFluxBots(true);
    try {
      const res = await FlowService.getFluxBots();

      setActiveTab({
        id: res.data[0].id ?? '',
        name: res.data[0].name ?? '',
        space: res.data[0].space ?? '',
      });

      setFluxBots(res.data);
    } catch (error: any) {
      console.error(error);
      setNotification({
        type: 'error',
        message: error?.message ?? 'Error while fetching flux bots',
      });
    } finally {
      setIsLoadingFluxBots(false);
    }
  };

  const getChatbot = async () => {
    setIsLoadingChatbot(true);
    try {
      const chatbotResponse = await ChatbotService.getChatbot(activeTab.id);
      setDropdownValues([]);

      if (
        !chatbotResponse.data.sessions.some(
          session => session.id.toLowerCase() === 'primary',
        )
      ) {
        chatbotResponse.data.sessions.push({
          id: 'primary',
          name: 'primary',
          entries: [],
        });
      }

      if (
        chatbotResponse.data.chatbotDropdowns &&
        chatbotResponse.data.chatbotDropdowns.length > 0
      ) {
        setDropdownValues(
          chatbotResponse.data.chatbotDropdowns.map(
            item => item.options[0].value,
          ),
        );
      }

      setChatbot(chatbotResponse.data);
    } catch (error: any) {
      console.error(error);
      setNotification({
        type: 'error',
        message: error?.message ?? 'Something went wrong',
      });
    } finally {
      setIsLoadingChatbot(false);
    }
  };

  const getChatSession = (sessionId: string) => {
    try {
      const chatSession = chatbot.sessions.find(
        session => session.id === sessionId,
      );

      if (chatSession) {
        setSession(chatSession.id);
      } else {
        const newSessionId =
          sessionId === 'primary' ? 'primary' : generateRandomId();

        const newChat: IChat = {
          id: newSessionId,
          name: newSessionId,
          entries:
            chatbot.firstMessage && chatbot.storeOpeningMessage !== 'as-title'
              ? [
                {
                  role: 'assistant',
                  content: chatbot.firstMessage,
                  timestamp: new Date().toISOString(),
                  actions: chatbot.chatActions ?? [],
                },
              ]
              : [],
        };

        setChatbot(prev => ({
          ...prev,
          sessions: [...prev.sessions, newChat],
        }));
        setSession(newSessionId);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handlePublish = async () => {
    try {
      const response = await FlowService.updateFlow(chatbot.id, {
        public: !chatbot.public,
      });

      const newChatbot = flowToChatbotMapper(response.data);

      setChatbot(newChatbot);

      setNotification({
        type: 'success',
        message: 'Published successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error publishing',
      });
      console.error(error);
    }
  };

  const handleClearConversation = async () => {
    if (!chatbot) {
      setNotification({
        type: 'error',
        message: 'Chatbot not set',
      });
      return;
    }

    try {
      await FlowService.clearChatFlow(chatbot.id, session);

      setChatbot(prev => {
        const updatedSessions = prev.sessions.map(chatSession => {
          if (chatSession.id === session) {
            return {
              ...chatSession,
              entries: [],
            };
          }

          return chatSession;
        });

        return { ...prev, sessions: updatedSessions };
      });

      setNotification({
        type: 'success',
        message: 'Chat cleared successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error clearing chat',
      });
      console.log(error);
    }
  };

  const handleCreateNewChat = () => {
    const sessionId = generateRandomId();

    const newChat: IChat = {
      id: sessionId,
      name: sessionId,
      entries:
        chatbot.firstMessage && chatbot.storeOpeningMessage !== 'as-title'
          ? [
            {
              role: 'assistant',
              content: chatbot.firstMessage,
              timestamp: new Date().toISOString(),
              actions: chatbot.chatActions ?? [],
            },
          ]
          : [],
    };

    setSession(sessionId);
    setChatbot(prev => ({
      ...prev,
      sessions: [...prev.sessions, newChat],
    }));
  };

  const handleCopyMessage = (message: string) => {
    navigator.clipboard.writeText(message);
  };

  const handleRecord = () => {
    try {
      const SpeechRecognition =
        window.SpeechRecognition ?? window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.error('Speech recognition not supported in this browser');
        return;
      }

      audioRecognitionRef.current = new SpeechRecognition();
      audioRecognitionRef.current.continuous = true;
      audioRecognitionRef.current.lang = 'en-US';

      audioRecognitionRef.current.onstart = () => {
        setInputValue('');
        setIsRecording(true);
      };

      audioRecognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      audioRecognitionRef.current.onerror = event => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      audioRecognitionRef.current.onresult = event => {
        const resultsLength = event.results.length;
        let transcript = '';
        setInputValue('');

        for (let i = 0; i < resultsLength; i++) {
          const result = event.results[i];
          transcript += result[0].transcript;
        }

        setInputValue(transcript);
      };

      audioRecognitionRef.current.start();
    } catch (error) {
      console.error('Error initializing speech recognition:', error);
      setIsRecording(false);
    }
  };

  const handleStopRecording = () => {
    if (!audioRecognitionRef.current) return;

    audioRecognitionRef.current.stop();
    setIsRecording(false);
  };

  const handleVoiceToggle = useCallback(() => {
    setIsChatRespondingWithVoice(prev => {
      return !prev;
    });
  }, []);

  const hasAudioLink = useCallback((text: string): boolean => {
    if (text.match(/\.(mp3|wav|ogg|m4a|aac|flac|webm)/i)) {
      return true;
    }

    return false;
  }, []);

  const handleSpeak = useCallback(
    (text: string) => {
      if (!isChatRespondingWithVoice) return;

      if (!hasAudioLink(text)) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.voice = voice;
        window.speechSynthesis.speak(utterance);
      }
    },
    [isChatRespondingWithVoice, voice, hasAudioLink],
  );

  const modifyChatCallback = useCallback(
    (resData: ChatSocketResponse) => {
      setChatbot(prev => {
        const updatedSessions = prev.sessions.map(chatSession => {
          if (chatSession.id === session) {
            return {
              ...chatSession,
              entries: [
                ...chatSession.entries,
                {
                  role: 'assistant',
                  content: resData.data[0].text,
                  timestamp: resData.data[0].timestamp,
                },
              ],
            };
          }

          return chatSession;
        });

        return { ...prev, sessions: updatedSessions };
      });

      setIsTyping(false);
      handleSpeak(resData.data[0].text);
    },
    [session, handleSpeak],
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = useCallback(
    async (message?: string) => {
      if (!chatbot) {
        setNotification({
          type: 'error',
          message: 'Chatbot not set',
        });
        return;
      }

      if (!inputValue.trim() && !message) return;

      const newMessage = {
        role: 'user',
        content: message ?? inputValue.trim(),
        timestamp: new Date().toISOString(),
      };

      setIsTyping(true);
      try {
        await FluxService.chatApi(chatbot.id, socketClient?.id!, {
          sessionId: session,
          variableInputs: [
            {
              inputText: newMessage.content,
            },
            ...dropdownValues.map((value, index) => ({
              inputId: chatbot.chatbotDropdowns?.[index]?.varInputNodeId,
              inputText: value,
            })),
          ],
        });

        setChatbot(prev => ({
          ...prev,
          sessions: prev.sessions.map(chatSession => {
            if (chatSession.id === session) {
              return {
                ...chatSession,
                entries: [...chatSession.entries, newMessage],
              };
            }

            return chatSession;
          }),
        }));

        setInputValue('');
      } catch (err) {
        console.log(err);
      }
    },
    [
      inputValue,
      chatbot,
      setNotification,
      session,
      socketClient,
      dropdownValues,
    ],
  );

  const handleSetChatbot = useCallback(
    (flow: IFlowModel) => {
      const chatbot = flowToChatbotMapper(flow);

      const sessionExists = chatbot.sessions.find(ses => ses.id === session);

      if (sessionExists) {
        setSession(sessionExists.id);
      } else {
        setSession(chatbot.sessions[0]?.id ?? 'primary');
      }

      setChatbot(chatbot);
    },
    [session],
  );

  // Scroll to bottom
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen, scrollToBottom, isTyping, chatbot, session]);

  // Get chatbot when active tab changes
  useEffect(() => {
    if (activeTab.id) {
      getChatbot();
    }
  }, [activeTab]);

  // Get flux bots
  useEffect(() => {
    getFluxBots();
  }, []);

  // Socket client
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL as string);

    socket.on('connect', () => {
      setSocketClient(socket);
    });

    socket.on('chatbot-req', (resData: any) => {
      modifyChatCallback(resData);
    });

    return () => {
      socket?.off('connect');
      socket?.off('chatbot-req');
    };
  }, [session, modifyChatCallback]);

  useEffect(() => {
    if (!isChatRespondingWithVoice) {
      window.speechSynthesis.cancel();
    }
  }, [isChatRespondingWithVoice]);

  // Get voices
  useEffect(() => {
    const voices = window.speechSynthesis.getVoices();
    let USVoice = null;

    USVoice = voices.find(voice => voice.lang === 'en-US');
    setVoice(USVoice ?? voices[0]);
  }, []);

  const currentSession = useMemo(() => {
    if (!chatbot.sessions || chatbot.sessions.length === 0) return null;

    return chatbot.sessions.find(chat => chat.id === session) ?? null;
  }, [chatbot, session]);

  const settingsOptions = [
    {
      key: 'clear-chat',
      label: 'Clear chat',
      icon: <Trash size={24} weight="regular" />,
      action: handleClearConversation,
      disabled: false,
    },
    {
      key: 'edit-bot',
      label: 'Edit FluxBot',
      icon: <NotePencil size={24} weight="regular" />,
      action: () => {
        setIsSettingsOpen(true);
      },
      disabled: false,
    },
    {
      key: 'auto-play',
      label: `Autoplay Audio ${isChatRespondingWithVoice ? 'Enabled' : 'Disabled'
        }`,
      icon: isChatRespondingWithVoice ? (
        <SpeakerHigh size={24} weight="regular" />
      ) : (
        <SpeakerSlash size={24} weight="regular" />
      ),
      action: handleVoiceToggle,
      disabled: false,
    },
    {
      key: 'publish-external',
      label: 'Publish externally',
      icon: <Play size={24} weight="regular" />,
      action: handlePublish,
      disabled: !!chatbot.public,
    },
    {
      key: 'copy-link',
      label: 'Copy link',
      icon: <Link size={24} weight="regular" />,
      action: () => {
        const linkChat = `${window.location.origin}/chatbot/${chatbot.id}`;
        navigator.clipboard.writeText(linkChat);
      },
      disabled: !chatbot.public,
    },
    {
      key: 'go-to-chat',
      label: 'Go to FluxBot',
      icon: <Globe size={24} weight="regular" />,
      action: () => {
        window.open(
          `${window.location.origin}/chatbot/${chatbot.id}`,
          '_blank',
        );
      },
      disabled: !chatbot.public,
    },
    {
      key: 'create-chat',
      label: 'Create new Chat',
      icon: <Plus size={24} weight="regular" />,
      action: handleCreateNewChat,
      disabled: false,
    },
  ];

  if (!isAbleToShowTabs) return null;

  return (
    <Box>
      <Box
        sx={{
          position: 'absolute',
          top: tabs.length <= 1 ? '8px' : '45px',
          right: '20px',
        }}
      >
        <IconButton onClick={toggleDrawer}>
          <Chats size={20} color="#3650DE" />
        </IconButton>
      </Box>

      <Drawer
        variant="persistent"
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer}
        hideBackdrop
        PaperProps={{
          sx: {
            width:
              expasionMode === 'middle'
                ? '600px'
                : window.innerWidth - sideBarOpenWidth,
            padding: 0,
            borderTop: `6px solid ${chatbot.brandColor}`,
            marginTop: tabs.length <= 1 ? '40px' : '80px',

            borderRadius: '8px 0 0 0',
          },
        }}
      >
        {isLoadingFluxBots && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <LoadingSpinner />
          </Box>
        )}

        {!isLoadingFluxBots && (
          <ChatContainer
            sx={{
              background: chatbot.pageBackground,
              color: chatbot.pageText,
              backgroundImage: `url(${chatbot.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              fontFamily: chatbot.font,
              fontWeight: chatbot.fontWeight,
              paddingBottom:
                tabs.length <= 1 ? 'calc(40px + 16px)' : 'calc(80px + 16px)',
            }}
          >
            <ChatHeader>
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  ref={historyPopoverAnchorEl}
                  onClick={() => setIsHistoryPopoverOpen(true)}
                  sx={{
                    color: chatbot.pageText,
                    '&:hover': {
                      color: chatbot.brandColor,
                    },
                  }}
                >
                  <TextAlignJustify
                    size={20}
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>

                <Box
                  ref={tabDropdownRef}
                  onClick={() => setIsFluxbotDropdownPopoverOpen(true)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: '#F2F4F7',
                    borderRadius: '8px',
                    padding: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: '#475467',
                    }}
                  >
                    {`${activeTab?.name ?? ''} (${activeTab?.space ?? ''})`}
                  </Typography>

                  <CaretDown
                    cursor="pointer"
                    color="#101828"
                    size={16}
                    weight="regular"
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    padding: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      color: chatbot.public ? '#4CAF50' : '#B42318',
                    }}
                  >
                    {chatbot.public ? 'Published' : 'Unpublished'}
                  </Typography>

                  {chatbot.public ? (
                    <CheckCircle color="#4CAF50" size={16} />
                  ) : (
                    <XCircle color="#B42318" size={16} />
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  onClick={toggleExpansionMode}
                  sx={{
                    color: chatbot.pageText,
                    '&:hover': {
                      color: chatbot.brandColor,
                    },
                  }}
                >
                  <Notebook
                    size={24}
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>

                <IconButton
                  ref={settingsPopoverAnchorEl}
                  onClick={() => setIsSettingsPopoverOpen(true)}
                  sx={{
                    color: chatbot.pageText,
                    '&:hover': {
                      color: chatbot.brandColor,
                    },
                  }}
                >
                  <Gear cursor={'pointer'} size={24} />
                </IconButton>

                <IconButton
                  onClick={toggleDrawer}
                  sx={{
                    color: chatbot.pageText,
                    '&:hover': {
                      color: chatbot.brandColor,
                    },
                  }}
                >
                  <XCircle
                    size={24}
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </Box>
            </ChatHeader>

            <ChatContent
              sx={{
                paddingBottom: tabs.length <= 1 ? 'calc(60px)' : 'calc(100px)',
              }}
            >
              {isLoadingChatbot ? (
                <LoadingSpinner />
              ) : (
                <>
                  {currentSession?.entries.length === 0 && (
                    <Box
                      sx={{
                        height: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: '50px',
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '16px',
                          padding: '5px',
                        }}
                      >
                        {chatbot.logo ? (
                          <Avatar
                            src={chatbot.logo}
                            alt={chatbot.name}
                            sx={{
                              width: '25px',
                              height: '25px',

                              '&.MuiAvatar-root': {
                                borderRadius: 0,
                              },
                            }}
                          />
                        ) : (
                          <Chats
                            size={50}
                            style={{
                              color: chatbot.pageText ?? '#fff',
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          maxWidth: '70%',
                          textAlign: 'center',
                          fontSize: '1.5rem',
                          lineHeight: '133%',
                          color: chatbot.pageText,
                          fontWeight: 'bold',
                        }}
                      >
                        {chatbot.storeOpeningMessage === 'as-title' &&
                          chatbot.firstMessage
                          ? chatbot.firstMessage
                          : chatbot.name}
                      </Typography>

                      {chatbot.storeOpeningMessage === 'as-title' &&
                        chatbot.chatActions &&
                        chatbot.chatActions.length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              mt: 2,
                              width: '100%',
                              maxWidth: '400px',
                            }}
                          >
                            {chatbot.chatActions.map(action => (
                              <Button
                                sx={{
                                  background: chatbot.brandColor,
                                  width: '100%',
                                  borderRadius: '8px',
                                  padding: '8px 24px',
                                  border: `1px solid ${chatbot.brandColor}`,
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  '&:hover': {
                                    background: '#fff',
                                    color: chatbot.brandColor,
                                  },
                                }}
                                key={action.name}
                                onClick={() =>
                                  handleSendMessage(action.description)
                                }
                                disabled={isTyping}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {action.name}
                                </Typography>
                                <ArrowRight size={16} />
                              </Button>
                            ))}
                          </Box>
                        )}
                    </Box>
                  )}

                  <MessagesContainer>
                    {currentSession?.entries.map((message, index) => {
                      const isNewMessage =
                        index === currentSession.entries.length - 1 &&
                        message.role === 'assistant';

                      return (
                        <MessageContainer
                          key={`${message.timestamp}-${index}`}
                          isUser={message.role === 'user'}
                        >
                          {message.role === 'assistant' && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius:
                                  chatbot.iconShape === 'circle'
                                    ? '50%'
                                    : '8px',
                                padding: '4px',
                              }}
                            >
                              {chatbot.chatImage ? (
                                <Avatar
                                  src={chatbot.chatImage}
                                  alt={chatbot.name}
                                  sx={{
                                    width: `${chatbot.iconSize}px`,
                                    height: `${chatbot.iconSize}px`,
                                    '&.MuiAvatar-root': {
                                      borderRadius:
                                        chatbot.iconShape === 'circle'
                                          ? '50%'
                                          : 0,
                                    },
                                  }}
                                />
                              ) : (
                                <Chats
                                  color={chatbot.pageText ?? '#fff'}
                                  style={{
                                    width: `${chatbot.iconSize}px`,
                                    height: `${chatbot.iconSize}px`,
                                  }}
                                />
                              )}
                            </Box>
                          )}

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            <MessageBubble
                              isUser={message.role === 'user'}
                              sx={{
                                backgroundColor:
                                  message.role === 'user'
                                    ? chatbot.userContainer
                                    : chatbot.responseContainer,
                                color:
                                  message.role === 'user'
                                    ? chatbot.userText
                                    : chatbot.responseText,
                                fontSize: '1rem',
                              }}
                            >
                              <div className={'chatbot-message-content'}>
                                <MessageContent
                                  content={message.content}
                                  messageTimestamp={message.timestamp}
                                  isNewMessage={isNewMessage}
                                  isChatRespondingWithVoice={
                                    isChatRespondingWithVoice
                                  }
                                />
                              </div>
                            </MessageBubble>

                            {message.role !== 'user' && !message.actions && (
                              <Button
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  backgroundColor: chatbot.responseContainer,
                                  width: 'fit-content',
                                  borderRadius: '8px',
                                  padding: '8px',
                                  color: chatbot.responseText,
                                  '&:hover': {
                                    color: '#fff',
                                    backgroundColor: chatbot.brandColor,
                                  },
                                }}
                                onClick={() =>
                                  handleCopyMessage(message.content)
                                }
                              >
                                Copy <Copy size={16} weight="regular" />
                              </Button>
                            )}

                            {message.actions && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}
                              >
                                {message.actions.map(action => (
                                  <Button
                                    sx={{
                                      background: chatbot.brandColor,
                                      width: 'fit-content',
                                      borderRadius: '8px',
                                      padding: '8px 24px',
                                      border: `1px solid ${chatbot.brandColor}`,
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'start',
                                      gap: 4,
                                      '&:hover': {
                                        background: '#fff',
                                        color: chatbot.brandColor,
                                      },
                                    }}
                                    key={action.name}
                                    disabled={isTyping}
                                    onClick={() => {
                                      handleSendMessage(action.description);
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {action.name}
                                    </Typography>

                                    <ArrowRight size={16} />
                                  </Button>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </MessageContainer>
                      );
                    })}

                    {isTyping && (
                      <MessageBubble
                        isUser={false}
                        sx={{
                          width: 'fit-content',
                          backgroundColor: chatbot.responseContainer,
                        }}
                      >
                        <TypingIndicator>
                          <span
                            className="dot"
                            style={{
                              backgroundColor:
                                chatbot.responseText ?? undefined,
                            }}
                          />
                          <span
                            className="dot"
                            style={{
                              backgroundColor:
                                chatbot.responseText ?? undefined,
                            }}
                          />
                          <span
                            className="dot"
                            style={{
                              backgroundColor:
                                chatbot.responseText ?? undefined,
                            }}
                          />
                        </TypingIndicator>
                      </MessageBubble>
                    )}
                  </MessagesContainer>

                  <div style={{ height: '5px' }} ref={messagesEndRef} />
                </>
              )}
            </ChatContent>

            <InputContainer
              sx={{
                background: chatbot.backgroundImage ? 'transparent' : 'inherit',
                bottom: tabs.length <= 1 ? 'calc(40px)' : 'calc(80px)',
              }}
            >
              {isRecording ? (
                <WaveformVisualizer
                  handleStopRecording={handleStopRecording}
                  isRecording={isRecording}
                  backgroundColor={chatbot.responseContainer ?? '#fff'}
                  waveColor={chatbot.brandColor ?? '#000'}
                />
              ) : (
                <>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    placeholder={chatbot.placeholder ?? 'Type your message...'}
                    maxRows={4}
                    onKeyDown={handleKeyPress}
                    disabled={isLoadingChatbot || isTyping}
                    InputProps={{
                      sx: {
                        color: chatbot.responseText,
                        fontFamily: chatbot.font,
                        fontWeight: chatbot.fontWeight,
                        '-webkit-text-fill-color': chatbot.responseText,
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={handleRecord}
                            disabled={isLoadingChatbot || isTyping}
                          >
                            {isRecording ? (
                              <Stop color="red" size={20} weight="regular" />
                            ) : (
                              <Microphone
                                style={{
                                  color: chatbot.brandColor ?? 'primary',
                                }}
                                size={20}
                                weight="regular"
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            onClick={() => handleSendMessage()}
                            disabled={
                              isLoadingChatbot || isTyping || !inputValue.trim()
                            }
                            sx={{
                              margin: 0,
                              padding: 0,
                              minWidth: 0,
                              minHeight: 0,
                              width: '24px',
                              height: '24px',
                              background: chatbot.brandColor,
                              '&:hover': {
                                background: chatbot.brandColor,
                              },
                            }}
                          >
                            <ArrowUp size={16} weight="regular" />
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      background: chatbot.responseContainer,
                      color: chatbot.responseText,
                      borderRadius: '8px',

                      '& .MuiInputBase-root:hover fieldset': {
                        borderColor: chatbot.brandColor,
                      },
                      '& .MuiInputBase-root:focus fieldset': {
                        borderColor: chatbot.brandColor,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: chatbot.brandColor,
                      },
                      '& .MuiInputBase-root fieldset': {
                        borderColor: chatbot.inputBorderColor,
                      },
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: '8px',
                    }}
                  >
                    {chatbot?.chatbotDropdowns?.map((item, index) => (
                      <Select
                        sx={{
                          background: chatbot.responseContainer,
                          color: chatbot.responseText,
                          '& fieldset': {
                            border: 'none',
                          },
                        }}
                        key={item.varInputNodeId}
                        value={dropdownValues[index]}
                        onChange={e => {
                          setDropdownValues(prev => {
                            const newValues = [...prev];
                            newValues[index] = e.target.value;
                            return newValues;
                          });
                        }}
                      >
                        {item.options.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ))}
                  </Box>
                </>
              )}
            </InputContainer>
          </ChatContainer>
        )}
      </Drawer>

      <Popover
        id="fluxbot-dropdown"
        open={isFluxbotDropdownPopoverOpen}
        onClose={() => {
          setIsFluxbotDropdownPopoverOpen(false);
        }}
        anchorEl={tabDropdownRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <FluxbotDropdown
          fluxBots={fluxBots}
          activeTab={activeTab}
          onClickSelectFluxBot={item => {
            setIsFluxbotDropdownPopoverOpen(false);
            setActiveTab({
              id: item.id,
              name: item.name,
              space: item.space,
            });
          }}
        />
      </Popover>

      <Popover
        id="fluxbot-settings"
        open={isSettingsPopoverOpen}
        onClose={() => {
          setIsSettingsPopoverOpen(false);
        }}
        anchorEl={settingsPopoverAnchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <List>
          {settingsOptions.map(option => (
            <ListItem disablePadding key={option.key}>
              <ListItemButton
                disabled={option.disabled}
                onClick={() => {
                  setIsSettingsPopoverOpen(false);
                  option.action();
                }}
              >
                <ListItemIcon sx={{ fontSize: 14 }}>{option.icon}</ListItemIcon>

                <ListItemText sx={{ fontSize: 14 }}>
                  {option.label}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>

      <Popover
        open={isHistoryPopoverOpen}
        onClose={() => setIsHistoryPopoverOpen(false)}
        anchorEl={historyPopoverAnchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <History
          chatbot={chatbot}
          currentSessionId={session}
          onChangeSession={sessionId => {
            getChatSession(sessionId);
          }}
        />
      </Popover>

      {isSettingsOpen && (
        <PublishChatModal
          show={isSettingsOpen}
          onHide={() => setIsSettingsOpen(false)}
          setFlow={handleSetChatbot}
          chatData={chatbot}
        />
      )}
    </Box>
  );
};

export default ChatBot;
